import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { font, fontControl } from '../../styles/typography';
import { RequiredStar, withRequiredBar, isFilled } from './requiredAccent';

const Field = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  ${font('md')}
  font-weight: 500;
  color: var(--spark-color-text-label);
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-control-border)')};
  border-radius: 6px;
  ${fontControl('base')}
  color: var(--spark-color-text-control);
  background: ${(p) => (p.$error ? 'var(--spark-color-error-subtle-surface)' : 'var(--spark-color-surface)')};
  box-sizing: border-box;
  box-shadow: ${(p) => withRequiredBar(p)};
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-focus)')};
    box-shadow: ${(p) => withRequiredBar(p, `0 0 0 2px ${p.$error ? 'var(--spark-color-error-ring)' : 'var(--spark-color-focus-ring)'}`)};
  }

  &:disabled {
    background: var(--spark-color-control-disabled-surface);
    color: var(--spark-color-control-disabled-text);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-control-border)')};
  border-radius: 6px;
  ${fontControl('base')}
  color: var(--spark-color-text-control);
  background: var(--spark-color-surface);
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  max-height: 240px;
  overflow-y: auto;
  box-sizing: border-box;
  box-shadow: ${(p) => withRequiredBar(p)};
  outline: none;

  &:focus {
    border-color: ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-focus)')};
    box-shadow: ${(p) => withRequiredBar(p, `0 0 0 2px ${p.$error ? 'var(--spark-color-error-ring)' : 'var(--spark-color-focus-ring)'}`)};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-control-border)')};
  border-radius: 6px;
  ${fontControl('base')}
  color: var(--spark-color-text-control);
  background: var(--spark-color-surface);
  box-sizing: border-box;
  box-shadow: ${(p) => withRequiredBar(p)};
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: ${(p) => (p.$error ? 'var(--spark-color-error-text)' : 'var(--spark-color-focus)')};
  }
`;

const ErrorText = styled.div`
  ${font('sm')}
  color: var(--spark-color-error-text);
  margin-top: 4px;
`;

export default function FormField({
  label,
  name,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
  accent, // opt-in: смуга зліва + крупна зірочка для обов'язкових полів
  error,
  disabled,
  options, // for select
  as: asProp,
  onBlur,
}) {
  const id = `field-${name}`;
  const errorId = `${id}-error`;
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);

  if (asProp === 'select' || options) {
    return (
      <Field>
        <Label htmlFor={id}>{label}{required && (accent ? <RequiredStar /> : ' *')}</Label>
        <Select
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange?.(name, e.target.value)}
          $error={error} $accent={accent && required} $filled={isFilled(value)}
          disabled={disabled}
        >
          <option value="">— Оберіть —</option>
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        {error && <ErrorText id={errorId}>{error}</ErrorText>}
      </Field>
    );
  }

  if (type === 'textarea') {
    return (
      <Field>
        <Label htmlFor={id}>{label}{required && (accent ? <RequiredStar /> : ' *')}</Label>
        <TextArea
          ref={textareaRef}
          id={id}
          value={value ?? ''}
          onChange={(e) => onChange?.(name, e.target.value)}
          $error={error} $accent={accent && required} $filled={isFilled(value)}
          disabled={disabled}
          placeholder={placeholder}
        />
        {error && <ErrorText id={errorId}>{error}</ErrorText>}
      </Field>
    );
  }

  return (
    <Field>
      <Label htmlFor={id}>{label}{required && (accent ? <RequiredStar /> : ' *')}</Label>
      <Input
        id={id}
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange?.(name, e.target.value)}
        $error={error} $accent={accent && required} $filled={isFilled(value)}
        disabled={disabled}
        placeholder={placeholder}
        onBlur={onBlur}
      />
      {error && <ErrorText id={errorId}>{error}</ErrorText>}
    </Field>
  );
}
